import axios from 'axios';
import {
  IAppConfig,
  IAppVersion,
  IProject,
  ICurrentUser,
  IUsers
} from '../data-models/dataModels';
import store from '../redux/store';

const ApplicationService = {
  getAppConfig: async (): Promise<IAppConfig> => {
    const response = await axios.get<IAppConfig>(
      `/config/AppConfigClient.json`,
      {
        headers: { 'Cache-Control': 'no-cache' }
      }
    );
    return response.data;
  },
  getAppVersion: async (): Promise<IAppVersion> => {
    const response = await axios.get<IAppVersion>(`/config/version.json`, {
      headers: { 'Cache-Control': 'no-cache' }
    });
    return response.data;
  },
  getPermissions: async (): Promise<string[]> => {
    const {
      config: { baseUrl }
    } = store.getState();

    const response = await axios.get<string[]>(
      `${baseUrl}/api/users/permissions`,
      { withCredentials: true }
    );
    return response.data;
  },
  getUser: async (projectName?: string): Promise<any> => {
    const {
      config: { baseUrl }
    } = store.getState();
    let header = {};
    if (projectName) {
      header = {
        projectName
      };
    }

    const response = await axios.get<ICurrentUser>(
      `${baseUrl}/api/users/currentUser`,
      { withCredentials: true, headers: header }
    );
    if (response.data.csrfToken) {
      axios.defaults.headers.common['X-XSRF-TOKEN'] = response.data.csrfToken;
    }
    return response.data;
  },
  getUsers: async (): Promise<IUsers[]> => {
    const {
      config: { baseUrl },
    } = store.getState();

    const response = await axios.get(
      `${baseUrl}/api/comment/getUsers`,
      { withCredentials: true } // Sending session cookies
    );
    return response.data;
  },
  getProjectList: async (projectName: string = "", logo: boolean = true): Promise<IProject[]> => {
    const {
      config: { baseUrl }
    } = store.getState();
    let header = {};
    if (projectName) {
      header = {
        projectName
      };
    }
    const response = await axios.get<IProject[]>(
      `${baseUrl}/api/users/getProjectList${"?logo="+logo}`,
      { withCredentials: true, headers: header }
    );
    return response.data;
  },

  getAskForFeedback: async (): Promise<boolean> => {
    const {
      config: { baseUrl }
    } = store.getState();
    const response = await axios.get<{ askForFeedback: boolean }>(
      `${baseUrl}/api/users/askForFeedback`,
      { withCredentials: true }
    );
    return !!response.data?.askForFeedback;
  },

  submitFeedback: async (rating: number, comment?: string, dontAskAgain?: boolean): Promise<{ success: boolean }> => {
    const {
      config: { baseUrl }
    } = store.getState();
    const payload = { rating, comment, dontAskAgain };
    const response = await axios.post<{ success: boolean }>(
      `${baseUrl}/api/users/feedback`,
      payload,
      { withCredentials: true }
    );
    return response.data;
  },

  setFavouriteModel: async (
    modelGroup: number,
  ): Promise<{ message: string; action: string }> => {
    const {
      config: { baseUrl }
    } = store.getState();

    const response = await axios.post<{ message: string; action: string }>(
      `${baseUrl}/api/plants/favorites/${modelGroup}`,
      {},
      { withCredentials: true }
    );
    return response.data;
  },

  setFavouriteSubModel: async (
    subModelId: number,
  ): Promise<{ message: string; action: string }> => {
    const {
      config: { baseUrl }
    } = store.getState();

    const response = await axios.post<{ message: string; action: string }>(
      `${baseUrl}/api/plants/favorites/submodel/${subModelId}`,
      {},
      { withCredentials: true }
    );
    return response.data;
  },
  getNotificationData: async (): Promise<any> => {
    const {
      config: { baseUrl }
    } = store.getState();

    const response = await axios.get<string>(
      `${baseUrl}/api/users/notification-data`,
      { withCredentials: true }
    );
    return response.data;
  },
  getWPCreatedByUser : async(createdBy : number,id:number) : Promise<any> => {
    const {
      config: { baseUrl }
    } = store.getState();
    try {
      const response = await axios.get(
        `${baseUrl}/api/users/getcreatedByWPName/` + createdBy + '/' + id,
        { withCredentials: true } // Sending session cookies
      );
      return response.data;
    } catch (e) {
      throw new Error(JSON.stringify((e as any).response.data));
    }
  },
  getAreaCreatedByUser : async(createdBy : number,id:number) : Promise<any> => {
    const {
      config: { baseUrl }
    } = store.getState();
    try {
      const response = await axios.get(
        `${baseUrl}/api/users/getcreatedByAreaName/` + createdBy + '/' + id,
        { withCredentials: true } // Sending session cookies
      );
      return response.data;
    } catch (e) {
      throw new Error(JSON.stringify((e as any).response.data));
    }
  },
  getFeaturAccessForExternal : async(email : string) : Promise<any> => {
    const {
      config: { baseUrl }
    } = store.getState();
    try{
      const response = await axios.get(
        `${baseUrl}/api/users/featureAcces`,
        { withCredentials: true , params : { email : email }}
      );
      return response.data; 
    } catch(e) {
      throw new Error(JSON.stringify((e as any).response.data));
    }
   
  },
 
  checkTokenIsPresent : async(email : string) : Promise<any> => {
    const {
      config: { baseUrl }
    } = store.getState();
    try{
      const response = await axios.get(
        `${baseUrl}/api/users/checkTokenPresent/` + email,
        { withCredentials: true }
      );
      return response.data; 
    } catch(e) {
      throw new Error(JSON.stringify((e as any).response.data));
    }
  },

  getExternalUserData: async (email:string) => {
    const {
      config: { baseUrl }
    } = store.getState();
    const response = await axios.post(
      `${baseUrl}/api/getExternalUser/${email}`,
      { withCredentials: true } // Sending session cookies
    );
    return response.data;
  },

  insertUserLogin: async () => {
    const {
      config: { baseUrl }
    } = store.getState();
    const response = await axios.post(
      `${baseUrl}/api/users/insertUserLogin`,
      {},
      { withCredentials: true }
    );
    return response.data;
  },
  
  getAllWpTypes: async() : Promise<any> =>{
    const {
      config: { baseUrl }
    } = store.getState(); 
    try{
       const response = await axios.get(
          `${baseUrl}/api/users/workpackageTypes`,
          { withCredentials: true }
        );
      return response.data;
    }catch(err){
      throw new Error(JSON.stringify((err as any).response.data));
    } 
  },
   getRestrictedExtesnionList: async(email : string | null, userName : string) => {
     const {
      config: { baseUrl }
    } = store.getState();
    const response = await axios.get(
      `${baseUrl}/api/users/getUserRulesPermissions/${email}/${userName}`,
      { withCredentials: true }
    );
    return response.data;
  }
  
};

export default ApplicationService;
